import classNames from "classnames";
import CiroButton, { CiroButtonStyleEnum } from "../../shared/CiroButton";
import CuratedSearchFilterJobTitleSection from "./CuratedSearchFilterSection/CuratedSearchFilterJobTitleSection";
import { LinkedinSalesNavigatorFilters } from "../../../__generated__/graphql";
import { useState } from "react";
import CuratedSearchFilterCompanyHeadcountSection from "./CuratedSearchFilterSection/LinkedinSalesNavigatorFilters/CuratedSearchFilterCompanyHeadcountSection";
import CuratedSearchFilterCurrentCompanySection from "./CuratedSearchFilterSection/CuratedSearchFilterCurrentCompanySection";
import CuratedSearchFilterIndustrySection from "./CuratedSearchFilterSection/LinkedinSalesNavigatorFilters/CuratedSearchFilterIndustrySection";
import CuratedSearchFilterGeographySection from "./CuratedSearchFilterSection/LinkedinSalesNavigatorFilters/CuratedSearchFilterGeographySection";
import CuratedSearchFilterCompanyHqSection from "./CuratedSearchFilterSection/LinkedinSalesNavigatorFilters/CuratedSearchFilterCompanyHqSection";
import { toast } from "react-hot-toast";
import { hasFiltersApplied } from "../curatedSearchUtils";
import {
  AdjustmentsVerticalIcon,
  SparklesIcon,
} from "@heroicons/react/20/solid";
import CiroTextInput from "../../shared/CiroTextInput";
import CuratedSearchFilterKeyWordsSection from "./CuratedSearchFilterSection/CuratedSearchFilterKeyWordsSection";
import sendPayloadToExtension from "../../../utils/sendPayloadToExtension";
import { CHECK_EXTENSION_INSTALLED } from "../../../utils/sendPayloadToExtension";
import CiroLink from "../../shared/CiroLink";
import { XMarkIcon } from "@heroicons/react/24/outline";

const SCORING_CRITERIA_MIN_LENGTH = 3;

const CuratedLinkedinSalesNavigatorSearchFiltersCard = ({
  additionalFiltering,
  setAdditionalFiltering,
  currentFilters,
  setCurrentFilters,
  onSave,
}: {
  additionalFiltering: string[];
  setAdditionalFiltering: (filtering: string[]) => void;
  currentFilters: LinkedinSalesNavigatorFilters | null;
  setCurrentFilters: (filters: LinkedinSalesNavigatorFilters) => void;
  onSave: () => Promise<void>;
}) => {
  // Track whether validation errors should be shown
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Validate scoring criteria
  const scoringCriteriaErrors = additionalFiltering.map((filter) => {
    if (!filter.trim()) {
      return "Scoring criteria cannot be empty";
    }
    if (filter.trim().length < SCORING_CRITERIA_MIN_LENGTH) {
      return `Scoring criteria must be at least ${SCORING_CRITERIA_MIN_LENGTH} characters`;
    }
    return null;
  });

  const hasScoringCriteriaErrors = scoringCriteriaErrors.some(
    (error) => error !== null,
  );

  return (
    <div className={classNames("w-full", "pt-4")}>
      {/* Header */}
      <div
        className={classNames(
          "flex",
          "justify-between",
          "items-center",
          "mb-4",
        )}
      >
        <div className={classNames("flex", "items-center", "gap-2")}>
          <AdjustmentsVerticalIcon
            className={classNames("h-5", "w-5", "text-orange-500")}
          />
          <h2 className={classNames("text-lg", "font-semibold")}>
            Selected Filters
          </h2>
        </div>
      </div>

      {/* Filters */}
      <div
        className={classNames("grid", "grid-cols-1", "xl:grid-cols-2", "gap-2")}
      >
        <CuratedSearchFilterKeyWordsSection
          defaultOpen={false}
          currentKeyWordsFilter={currentFilters?.keywords || ""}
          setCurrentKeyWordsFilter={(v) => {
            setCurrentFilters({ ...(currentFilters || {}), keywords: v });
          }}
        />
        <CuratedSearchFilterJobTitleSection
          defaultOpen={false}
          jobTitleFilter={currentFilters?.jobTitles || []}
          setJobTitleFilter={(v) => {
            setCurrentFilters({
              ...currentFilters,
              jobTitles: v,
            });
          }}
        />
        <CuratedSearchFilterCurrentCompanySection
          defaultOpen={false}
          currentCompanyFilter={
            (currentFilters?.companyNames ?? []) as {
              label: string;
            }[]
          }
          excludedCompanyFilter={
            (currentFilters?.excludedCompanyNames ?? []) as {
              label: string;
            }[]
          }
          setCurrentCompanyFilter={(v) => {
            setCurrentFilters({
              ...currentFilters,
              companyNames: v,
            });
          }}
          setExcludedCompanyFilter={(v) => {
            setCurrentFilters({
              ...currentFilters,
              excludedCompanyNames: v,
            });
          }}
        />
        <CuratedSearchFilterCompanyHeadcountSection
          defaultOpen={false}
          companyHeadcountFilter={
            (currentFilters?.companyHeadCounts || []) as {
              id: string;
              label: string;
            }[]
          }
          setCompanyHeadcountFilter={(v) => {
            setCurrentFilters({
              ...currentFilters,
              companyHeadCounts: v,
            });
          }}
        />
        <CuratedSearchFilterCompanyHqSection
          defaultOpen={false}
          currentCompanyHqFilter={
            (currentFilters?.companyHQLocations ?? []) as {
              label: string;
              id: number;
            }[]
          }
          setCurrentCompanyHqFilter={(v) => {
            setCurrentFilters({
              ...currentFilters,
              companyHQLocations: v,
            });
          }}
        />
        <CuratedSearchFilterGeographySection
          defaultOpen={false}
          currentGeographyFilter={
            (currentFilters?.geographies ?? []) as {
              label: string;
              id: number;
            }[]
          }
          setCurrentGeographyFilter={(v) => {
            setCurrentFilters({
              ...currentFilters,
              geographies: v,
            });
          }}
        />
        <CuratedSearchFilterIndustrySection
          defaultOpen={false}
          currentIndustryFilter={
            (currentFilters?.industries ?? []) as {
              label: string;
              id: number;
            }[]
          }
          setCurrentIndustryFilter={(v) => {
            setCurrentFilters({
              ...currentFilters,
              industries: v,
            });
          }}
        />
      </div>

      {/* Scoring Criteria */}
      <div className={classNames("pt-6", "flex", "flex-col", "gap-2")}>
        <div className={classNames("flex", "items-center", "gap-2")}>
          <SparklesIcon
            className={classNames("h-6", "w-6", "text-orange-500")}
          />
          <div>
            <div className={classNames("font-semibold")}>Scoring criteria</div>
          </div>
        </div>
        <div>
          {additionalFiltering.length > 0 && (
            <div>
              {additionalFiltering.map((filter, idx) => (
                <div
                  key={`filter-container-${idx}`}
                  className={classNames(
                    "flex",
                    "items-center",
                    "gap-4",
                    "py-2",
                    "px-1",
                    "text-sm",
                  )}
                >
                  <CiroTextInput
                    key={idx}
                    wrapperClassName={classNames("w-full")}
                    value={filter}
                    error={
                      showValidationErrors ? scoringCriteriaErrors[idx] : null
                    }
                    onChange={(e) => {
                      const newAdditionalFiltering = [...additionalFiltering];
                      newAdditionalFiltering[idx] = e.target.value;
                      setAdditionalFiltering(newAdditionalFiltering);

                      // Clear validation errors if all criteria are valid after editing
                      if (showValidationErrors) {
                        const newErrors = newAdditionalFiltering.map(
                          (filter) => {
                            if (!filter.trim())
                              return "Scoring criteria cannot be empty";
                            if (
                              filter.trim().length < SCORING_CRITERIA_MIN_LENGTH
                            )
                              return `Scoring criteria must be at least ${SCORING_CRITERIA_MIN_LENGTH} characters`;
                            return null;
                          },
                        );
                        if (!newErrors.some((error) => error !== null)) {
                          setShowValidationErrors(false);
                        }
                      }
                    }}
                  />
                  <CiroButton
                    analyticsField={"Removing scoring criteria"}
                    style={CiroButtonStyleEnum.UNSTYLED}
                    onClick={() => {
                      const newAdditionalFiltering = [...additionalFiltering];
                      newAdditionalFiltering.splice(idx, 1);
                      setAdditionalFiltering(newAdditionalFiltering);
                    }}
                  >
                    <XMarkIcon
                      className={classNames("w-5", "text-neutral-500")}
                    />
                  </CiroButton>
                </div>
              ))}
            </div>
          )}

          <div className={classNames("mt-2")}>
            <CiroButton
              analyticsField={"Add scoring criteria"}
              style={CiroButtonStyleEnum.PRIMARY}
              onClick={() => {
                const newAdditionalFiltering = [...additionalFiltering, ""];
                setAdditionalFiltering(newAdditionalFiltering);
              }}
            >
              <div className={classNames("flex", "items-center", "gap-2")}>
                <span>+</span>
                <span>Add criterion</span>
              </div>
            </CiroButton>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={classNames("flex", "justify-end", "gap-2", "mt-6")}>
        <CiroButton
          analyticsField="Save Sales Navigator Filters"
          style={CiroButtonStyleEnum.LOUD}
          disabled={!hasFiltersApplied({ filters: currentFilters })}
          onClick={async () => {
            if (!currentFilters) {
              toast.error("No filters applied");
              return;
            }
            if (hasScoringCriteriaErrors) {
              setShowValidationErrors(true);
              toast.error("Please fix errors in scoring criteria");
              return;
            }
            try {
              await sendPayloadToExtension({
                payload: {
                  action: CHECK_EXTENSION_INSTALLED,
                },
              });
              await onSave();
            } catch (error) {
              toast.error(
                <div>
                  <div>Chrome extension not found</div>
                  <div>
                    <CiroLink href="https://chromewebstore.google.com/detail/ciro/poiofceiibpjdmhihnpaembimmnioifk">
                      Download it here
                    </CiroLink>
                  </div>
                </div>,
              );
            }
          }}
        >
          Launch Sales Navigator
        </CiroButton>
      </div>
    </div>
  );
};

export default CuratedLinkedinSalesNavigatorSearchFiltersCard;
